import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToastHandler } from "@/hooks/useToastHandler";
import { authApi, AppleAuthData } from "@/api/authApi";
import { getDeviceTimezoneOffset } from "@/lib/dateUtils";
import { getInvitationTokenFromUrl, storeInvitationToken, retrieveAndClearInvitationToken } from "@/lib/invitationUtils";

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: Record<string, unknown>) => void;
        signIn: () => Promise<{
          authorization: {
            code: string;
            id_token: string;
            state: string;
          };
          user?: {
            name?: {
              firstName?: string;
              lastName?: string;
            };
            email?: string;
          };
        }>;
      };
    };
  }
}

const AppleLoginButton = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [appleApiLoaded, setAppleApiLoaded] = useState<boolean>(false);
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useToastHandler();

  useEffect(() => {
    // Check for invitation token in URL and store it
    const invitationToken = getInvitationTokenFromUrl();
    if (invitationToken) {
      storeInvitationToken(invitationToken);
    }

    // Load the Apple Sign-In API script
    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.defer = true;
    script.onload = initializeAppleAuth;
    document.body.appendChild(script);

    return () => {
      // Only remove the script if it still exists in the document
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const initializeAppleAuth = () => {
    if (!window.AppleID) return;

    try {
      // Initialize Apple Sign-In
      window.AppleID.auth.init({
        clientId: import.meta.env.VITE_APPLE_CLIENT_ID || "",
        scope: "name email",
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
      });

      setAppleApiLoaded(true);
    } catch (error) {
      console.error("...>>> Failed to initialize Apple Auth:", error);
      setAppleApiLoaded(false);
    }
  };

  const handleAppleLogin = async () => {
    if (!window.AppleID) {
      handleError(new Error("Apple Sign-In is not available"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await window.AppleID.auth.signIn();

      console.log(">>>response", response);

      // Retrieve stored invitation token if any
      const invitationToken = retrieveAndClearInvitationToken();

      // Extract the necessary data from the Apple response
      const appleAuthData: AppleAuthData = {
        idToken: response.authorization.id_token,
        deviceTimezoneOffset: getDeviceTimezoneOffset() || "-08:00",
        device: "web",
        ...(invitationToken && { invitation_token: invitationToken }),
        // authorizationCode: response.authorization.code,
        // user: response.user,
      };

      await authApi.appleLogin(appleAuthData);
      handleSuccess("Successfully logged in", 1000);
      navigate("/track");
    } catch (error) {
      console.error("...>>> Apple login error:", error);
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        onClick={handleAppleLogin}
        disabled={isLoading || !appleApiLoaded}
        className={`flex items-center justify-center gap-2 w-full py-2 px-4 border border-black rounded-md shadow-sm bg-black text-white hover:bg-gray-900 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
        style={{ minWidth: "240px" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 384 512"
          fill="currentColor"
        >
          {/* Apple logo icon */}
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
        </svg>
        <span>{isLoading ? "Logging in..." : "Sign in with Apple"}</span>
      </button>

      {/* Message when API isn't loaded */}
      {!appleApiLoaded && import.meta.env.DEV && (
        <p className="text-xs text-gray-500 mt-1">
          Note: Make sure VITE_APPLE_CLIENT_ID is set in your .env file
        </p>
      )}
    </div>
  );
};

export default AppleLoginButton;
